/**
 * The paging rule for a "games since" read (the signed-in home's list, and its Openings tile): the newest games first,
 * then older pages only while they're still inside the window. Paging the whole history instead (4,222 games for
 * cabbagelover5566, nine 500-game pages of ~10 s each) is what made the signed-in home slow (2026-10-12), and the
 * Openings tile still did it until #339 (ten pages, ~500 KB, after every home visit).
 */
import { mergePage, seedFromPrevious } from "./gamesPaging";

/** The UTC day `days` back from `now`, YYYY-MM-DD: GET /games?since=. Stored games are dated at midnight UTC,
 *  so this is a superset of a window that counts `days` x 24h back from now. */
export function sinceDate(now: Date, days: number): string {
  return new Date(now.getTime() - days * 86_400_000).toISOString().slice(0, 10);
}

interface SinceGame {
  readonly gameOriginId: string;
  readonly date?: string;
}

/** A since read as the cache holds it: the games shown, and how far this round of paging has got. */
export interface SinceRead<T extends SinceGame> {
  readonly games: readonly T[];
  readonly requiresUsername: boolean;
  /** This round's next page; null once there's none. */
  readonly nextToken: string | null;
  /** The date of the oldest game this round has fetched, null when it can't tell (none yet, or no date). Paging goes on
   *  while it's inside the window. Not the shown list's last game: after a refetch that's still the last round's. */
  readonly reached: string | null;
  /** How far back the list is known to hold every game, whatever round it's in (#348): the date of the oldest game a run
   *  of pages from the newest reached; "" once a page had nothing after it (every game of the read's window); null when
   *  unknown (a list persisted before #348). A refetch's first page that reaches the shown list's newest game keeps it,
   *  so the list stays whole while it's paged again; `coversWindow` reads it. */
  readonly covers: string | null;
}

/** One GET /games page, as listUserGames returns it. */
export interface GamesPage<T> {
  readonly games?: readonly T[];
  readonly requiresUsername?: boolean;
  readonly nextToken?: string | null;
}

const lastDate = <T extends SinceGame>(page: GamesPage<T>): string | null => page.games?.[page.games.length - 1]?.date ?? null;

/** The further back of two `covers`: "" (everything) beats any date, and a page whose last game has no date (null)
 *  doesn't move it. */
const further = (covers: string, page: string | null): string => (covers === "" || page == null || covers < page ? covers : page);

/** A round's first page (the newest games, unfiltered by date) over the list already shown: the page first, then every
 *  shown game it doesn't hold, so a refetch never shrinks the list (it used to drop to the first page until the older
 *  pages landed again). The paging restarts from this page. */
export function firstPage<T extends SinceGame>(prev: SinceRead<T> | undefined, page: GamesPage<T>): SinceRead<T> {
  const games = page.games ?? [];
  // The page reaches the shown list's newest game, so the two are one run from the newest game back.
  const joins = prev?.covers != null && prev.games.length > 0 && games.some((x) => x.gameOriginId === prev.games[0].gameOriginId);
  return {
    games: seedFromPrevious(prev?.games, games),
    requiresUsername: page.requiresUsername ?? false,
    nextToken: page.nextToken ?? null,
    reached: lastDate(page),
    covers: !page.nextToken ? "" : joins ? further(prev.covers as string, lastDate(page)) : lastDate(page),
  };
}

/** An older page into the read: a game already shown is refreshed where it is, an unseen one appended; the paging
 *  moves on. A page with no games (every lookup row without its record) keeps how far the round has reached. */
export function olderPage<T extends SinceGame>(current: SinceRead<T>, page: GamesPage<T>): SinceRead<T> {
  const covers = current.covers ?? null;
  return {
    games: mergePage(current.games, page.games ?? []),
    requiresUsername: false,
    nextToken: page.nextToken ?? null,
    reached: page.games?.length ? lastDate(page) : current.reached,
    covers: covers == null ? null : !page.nextToken ? "" : further(covers, page.games?.length ? lastDate(page) : null),
  };
}

/** Does a read whose window starts `readSince` hold every game on or after `since` (#348)? Its window must start no
 *  later, and the list must be known whole back past `since`: the Mistakes tab paints its window from the home's list
 *  only then. */
export function coversWindow(read: Pick<SinceRead<SinceGame>, "covers">, readSince: string, since: string): boolean {
  return readSince <= since && read.covers != null && read.covers.slice(0, 10) < since;
}

/** Fetch another page? Only when there is one and the oldest game this round has fetched is still on or after `since`.
 *  A game older than the window means everything inside it has arrived; one with no date can't tell, so stop. */
export function needsOlderGames(read: Pick<SinceRead<SinceGame>, "nextToken" | "reached">, since: string): boolean {
  return !!read.nextToken && !!read.reached && read.reached.slice(0, 10) >= since;
}
